import React, { useState, useCallback } from 'react'
import { View, ScrollView } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import NavigationTopbar from '../../NavigationTopbar'
import Txt from '../../Txt'
import InputFormik from '../../InputFormik'
import Button from '../../Button'
import LocationsSelectInput from '../../LocationsSelectInput'
import EquipmentsSelectInput from '../../EquipmentsSelectInput'
import { bookOrder, bookOffer } from '../../../redux/loadDetails/loadDetailsActions'
import useStyles from './useStyles'
import ConfirmationDialog from '../../ConfirmationDialog'
import { useTheme } from '../../../theming/ThemeProvider'
import KeyboardView from '../../KeyboardView'
import { usePlatforms } from '../../../hooks/usePlatforms'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { loadDetailSelector } from '../../../redux/loadDetails/loadDetailsSelector'

const initialValues = {
  tractorNumber: '',
  trailerNumber: '',
  trailerType: null,
  location: null,
  driverName: '',
  driverPhoneNumber: '',
  driverEmail: '',
  dispatcherName: '',
  dispatcherPhoneNumber: '',
  dispatcherEmail: ''
}

const validationSchema = Yup.object({
  dispatcherEmail: Yup.string()
    .email('Invalid email address')
    .required('Dispatcher email is required'),
  driverEmail: Yup.string()
    .nullable()
    .email('Invalid email address'),
  driverPhoneNumber: Yup.string()
    .nullable()
    .matches('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$', 'Invalid phone number'),
  dispatcherPhoneNumber: Yup.string()
    .nullable()
    .matches('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$', 'Invalid phone number')
})

const BookForm = (props) => {
  const { rate, orderNumber, movementNumber, offerHistory, module, onClose } = props
  const dispatch = useDispatch()
  const { loadingBook } = useSelector(loadDetailSelector)
  const { isTablet } = usePlatforms()
  const { theme } = useTheme()
  const styles = useStyles(theme, isTablet)

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(null)

  const handleBook = values => setConfirmationDialogOpen(values)

  const handleConfirmationDialog = useCallback((action) => {
    if (action) {
      const payload = {
        dispatch: {
          name: confirmationDialogOpen.dispatcherName,
          phone: confirmationDialogOpen.dispatcherPhoneNumber,
          email: confirmationDialogOpen.dispatcherEmail
        },
        driver: {
          name: confirmationDialogOpen.driverName,
          phone: confirmationDialogOpen.driverPhoneNumber,
          email: confirmationDialogOpen.driverEmail,
          tractor_number: confirmationDialogOpen.tractorNumber,
          trailer_number: confirmationDialogOpen.trailerNumber,
          trailer_type: confirmationDialogOpen.trailerType?.equipment_code || undefined,
          location_city: confirmationDialogOpen.location?.city || undefined,
          location_state: confirmationDialogOpen.location?.state_id || undefined
        }
      }
      const order = {
        order_id: orderNumber,
        movement_id: movementNumber,
        rate
      }
      if (module === 'all-loads' || module === 'my-loads') {
        dispatch(bookOrder({
          ...payload,
          order
        }))
      } else {
        dispatch(bookOffer({
          ...payload,
          offer: {
            ...order,
            offer_id: offerHistory[offerHistory?.length - 1].offer_id
          }
        }))
      }
    }
    setConfirmationDialogOpen(null)
  }, [confirmationDialogOpen, dispatch, module, movementNumber, offerHistory, orderNumber, rate])

  return (
    <KeyboardView>
      <NavigationTopbar
        onGoBack={onClose}
        containerStyle={styles.container}
      />
      <Formik
        initialValues={initialValues}
        onSubmit={handleBook}
        validationSchema={validationSchema}
      >
        {({ values, handleSubmit, errors, setFieldValue, touched }) => (
          <>
            <ScrollView style={styles.container}>
              <View style={styles.header}>
                <Txt
                  text='Book Order'
                  identity='h3'
                />
                <Txt
                  text='Please fill the information you find necessary to book this load.'
                  identity='body2'
                />
              </View>
              <Txt
                text='Capacity'
                identity='h4'
                style={styles.title}
              />
              <View style={styles.numbersContainer}>
                <View style={styles.tractorContainer}>
                  <InputFormik
                    name='tractorNumber'
                    label='Tractor Number'
                    placeholder='Tractor Number'
                    letfIcon='Tractors'
                    value={values?.tractorNumber || ''}
                    onChange={setFieldValue}
                    testID='bookFormTractorNumberInput'
                    accessibilityLabel='bookFormTractorNumberInput'
                  />
                </View>
                <View style={styles.trailerContainer}>
                  <InputFormik
                    name='trailerNumber'
                    label='Trailer Number'
                    placeholder='Triler Number'
                    letfIcon='Tractors'
                    value={values?.trailerNumber || ''}
                    onChange={setFieldValue}
                    testID='bookFormTrailerNumberInput'
                    accessibilityLabel='bookFormTrailerNumberInput'
                  />
                </View>
              </View>
              <LocationsSelectInput
                label='Location'
                name='location'
                placeholder='Location'
                selected={values?.location || ''}
                onChange={setFieldValue}
                origin
                style={styles.select}
              />
              <EquipmentsSelectInput
                label='Equipment Type'
                name='trailerType'
                selected={values?.trailerType || ''}
                onChange={setFieldValue}
                style={styles.select}
              />
              <Txt
                text='Driver'
                identity='h4'
                style={styles.title}
              />
              <InputFormik
                name='driverName'
                label='Driver Name'
                placeholder='Name'
                letfIcon='User'
                value={values?.driverName || ''}
                onChange={setFieldValue}
                testID='bookFormDriverNameInput'
                accessibilityLabel='bookFormDriverNameInput'
              />
              <InputFormik
                name='driverPhoneNumber'
                label='Driver Phone'
                placeholder='Phone Number'
                letfIcon='Phone'
                value={values?.driverPhoneNumber || ''}
                onChange={setFieldValue}
                keyboardType='phone-pad'
                error={touched?.driverPhoneNumber && errors?.driverPhoneNumber}
                testID='bookFormDriverPhoneInput'
                accessibilityLabel='bookFormDriverPhoneInput'
              />
              <InputFormik
                name='driverEmail'
                label='Driver Email'
                placeholder='Email'
                letfIcon='Email'
                value={values?.driverEmail || ''}
                onChange={setFieldValue}
                keyboardType='email-address'
                error={touched?.driverEmail && errors?.driverEmail}
                testID='bookFormDriverEmailInput'
                accessibilityLabel='bookFormDriverEmailInput'
              />
              <Txt
                text='Dispatcher'
                identity='h4'
                style={styles.title}
              />
              <InputFormik
                name='dispatcherName'
                label='Dispatcher Name'
                placeholder='Name'
                letfIcon='User'
                value={values?.dispatcherName || ''}
                onChange={setFieldValue}
                testID='bookFormDispatcherNameInput'
                accessibilityLabel='bookFormDispatcherNameInput'
                type='text'
              />
              <InputFormik
                name='dispatcherPhoneNumber'
                label='Dispatcher Phone'
                placeholder='Phone Number'
                letfIcon='Phone'
                value={values?.dispatcherPhoneNumber || ''}
                onChange={setFieldValue}
                keyboardType='phone-pad'
                error={touched?.dispatcherPhoneNumber && errors?.dispatcherPhoneNumber}
                testID='bookFormDispatcherPhoneInput'
                accessibilityLabel='bookFormDispatcherPhoneInput'
              />
              <InputFormik
                name='dispatcherEmail'
                label='Dispatcher Email'
                placeholder='Email'
                letfIcon='Email'
                value={values?.dispatcherEmail || ''}
                onChange={setFieldValue}
                keyboardType='email-address'
                error={touched?.dispatcherEmail && errors?.dispatcherEmail}
                testID='bookFormDispatcherEmailInput'
                accessibilityLabel='bookFormDispatcherEmailInput'
              />
            </ScrollView>
            <View style={styles.bottomSection}>
              <View style={styles.rateContainer}>
                <Txt
                  avoid
                  text='Current Rate'
                  identity='body2'

                />
                <View style={styles.rateBadge}>
                  <Txt
                    text={`${rate || '- -'} USD`}
                    identity='body2'
                    color='FERN'
                    selectable
                  />
                </View>
              </View>
              <Button
                title='Book'
                onPress={handleSubmit}
                loading={loadingBook}
                testID='bookFormButton'
                accessibilityLabel='bookFormButton'
                style={styles.button}
              />
            </View>
          </>
        )}
      </Formik>
      <ConfirmationDialog
        open={Boolean(confirmationDialogOpen)}
        title='Book Offer'
        description='Are sure you want to book this offer? An Edge dispatcher will be notified immediately.'
        onClose={handleConfirmationDialog}
      />
    </KeyboardView>
  )
}

BookForm.propTypes = {
  rate: PropTypes.string,
  orderNumber: PropTypes.string,
  movementNumber: PropTypes.string,
  offerHistory: PropTypes.array,
  module: PropTypes.string,
  onClose: PropTypes.func
}

export default BookForm
